/**
 * ScrollToBottomButton — floating "Jump to latest" affordance for chat scrollers.
 *
 * Pairs with the `useScrollToBottom()` hook. The hook tells you whether the
 * user has scrolled up; this button gives them a one-click way back. Default
 * icon is an `ArrowDown`; while AIKO is composing, consumers typically pass
 * `<TypingIndicator />` to render a live cue inside the button instead.
 *
 * The button is absolutely positioned by default so it floats above the
 * scroller — `absolute bottom-3 left-1/2 -translate-x-1/2 z-10`. Override the
 * positioning via `className` when you need a different placement (e.g.
 * bottom-right inside a sidebar). Visible/hidden state is controlled via the
 * `visible` prop wrapped in `<AnimatePresence>` for a quiet fade+slide.
 *
 * Unread badge: pass a positive integer in `unreadCount` to render a small
 * badge in the top-right corner ("Jump · 4 new"). Counts ≥100 render as "99+".
 *
 * Onyx press: this is a ROUND floating control, so it keeps its circle — the
 * Onyx hex notch is for rectangular surfaces only. It still speaks the shared
 * offset-key language via the press: a springy 1px press-DOWN on `:active` (a
 * CSS translate + overshoot ease, the same bounce every Button/IconButton uses)
 * so the whole lib presses consistently. No notch, no travel block, no keyline
 * ring. The press rides on the host class and is gated by `motion` +
 * `prefers-reduced-motion`, so it never fights the fade+slide entrance.
 *
 * Composition with `useScrollToBottom`:
 *
 *   const { scrollRef, sentinelRef, isAtBottom, scrollToBottom } = useScrollToBottom<HTMLDivElement>();
 *   <div ref={scrollRef} className="relative h-full overflow-y-auto">
 *     {messages.map(...)}
 *     <div ref={sentinelRef} aria-hidden />
 *     <ScrollToBottomButton
 *       visible={!isAtBottom}
 *       unreadCount={unreadCount}
 *       onClick={() => scrollToBottom()}
 *     />
 *   </div>
 */
import * as React from "react";
import { motion, AnimatePresence, type HTMLMotionProps } from "motion/react";
import { ArrowDown } from "lucide-react";
import { cn } from "../lib/cn";
import { useReducedMotion } from "../hooks/useReducedMotion";

/* ============================================================================
 * Types
 * ========================================================================= */

export interface ScrollToBottomButtonProps extends Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  "children"
> {
  /** Click handler — typically the `scrollToBottom` returned by `useScrollToBottom()`. */
  onClick: React.MouseEventHandler<HTMLButtonElement>;
  /** Whether the button is on-screen. Default `true`. Toggled by AnimatePresence. */
  visible?: boolean;
  /** Optional inner content. Defaults to an `ArrowDown` icon. */
  children?: React.ReactNode;
  /** When > 0, renders an unread-count pill in the top-right corner. */
  unreadCount?: number;
  /**
   * Disable the fade+slide motion on visible toggle. Default true.
   * `prefers-reduced-motion` forces this off regardless.
   */
  motion?: boolean;
  /** Screen-reader label. Defaults to "Jump to latest". */
  "aria-label"?: string;
}

/* ============================================================================
 * Component
 * ========================================================================= */

const BUTTON_BASE = cn(
  "absolute bottom-3 left-1/2 -translate-x-1/2 z-10",
  "inline-flex items-center justify-center",
  "h-9 w-9 rounded-full",
  "bg-foreground text-background shadow-lg border border-border",
  "transition-colors duration-150",
  "hover:bg-foreground/90",
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
  "ring-offset-background",
  "disabled:pointer-events-none disabled:opacity-50",
);

/**
 * The shared offset-key press for round controls: a springy 1px press-DOWN with
 * overshoot ease — the same bounce every Button/IconButton/KeycapButton uses, so
 * the whole lib presses consistently. Gated by `motion` + `prefers-reduced-motion`.
 */
const PRESS = "transition-transform duration-150 ease-[cubic-bezier(0.34,1.45,0.6,1)] active:translate-y-px";

function formatUnread(count: number) {
  if (count <= 0) return null;
  return count >= 100 ? "99+" : String(count);
}

export const ScrollToBottomButton = React.forwardRef<HTMLButtonElement, ScrollToBottomButtonProps>(
  (
    {
      className,
      onClick,
      visible = true,
      children,
      unreadCount,
      motion: enableMotion = true,
      "aria-label": ariaLabel = "Jump to latest",
      ...rest
    },
    ref,
  ) => {
    const reduced = useReducedMotion();
    const shouldAnimate = enableMotion && reduced !== true;
    const unreadLabel = formatUnread(unreadCount ?? 0);

    const inner = (
      <>
        {children ?? <ArrowDown className="size-4" aria-hidden />}
        {unreadLabel ? (
          <span
            className={cn(
              "absolute -top-1 -right-1 inline-flex h-4 min-w-4 items-center justify-center",
              "rounded-full px-1 text-[10px] font-semibold leading-none",
              "bg-accent text-accent-foreground shadow-sm",
            )}
            aria-hidden
          >
            {unreadLabel}
          </span>
        ) : null}
      </>
    );

    // The visible aria-label folds in the unread count so SR users hear
    // "Jump to latest, 3 unread" rather than just the static label.
    const composedAriaLabel = unreadLabel ? `${ariaLabel}, ${unreadLabel} unread` : ariaLabel;

    if (!shouldAnimate) {
      if (!visible) return null;
      return (
        <button
          ref={ref}
          type="button"
          onClick={onClick}
          aria-label={composedAriaLabel}
          className={cn(BUTTON_BASE, shouldAnimate && PRESS, className)}
          {...rest}
        >
          {inner}
        </button>
      );
    }

    return (
      <AnimatePresence>
        {visible ? (
          <motion.button
            ref={ref}
            type="button"
            onClick={onClick}
            aria-label={composedAriaLabel}
            className={cn(BUTTON_BASE, className)}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
            // Shared offset-key press: a springy 1px press-DOWN. Framer owns the
            // `transform` here (the fade+slide), so the CSS `active:` press can't
            // win — express the same press as a `whileTap` with the overshoot
            // bounce ease instead, keeping it consistent with the static path.
            whileTap={{ y: 1, transition: { duration: 0.15, ease: [0.34, 1.45, 0.6, 1] } }}
            {...(rest as HTMLMotionProps<"button">)}
          >
            {inner}
          </motion.button>
        ) : null}
      </AnimatePresence>
    );
  },
);
ScrollToBottomButton.displayName = "ScrollToBottomButton";
